// react
import { useEffect, useRef } from 'react';

// capacitor
import {
  App as CapacitorApp,
  AppLaunchUrl,
  URLOpenListenerEvent,
} from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { useOpenUrl } from 'hooks/useOpenUrl';

/**
 * UniversalLinkHandler listens for universal links and navigates to the
 * appropriate path.
 */
export const UniversalLinkHandler = () => {
  // refs
  const listenerRef = useRef<PluginListenerHandle | null>(null);
  const appUrlOpenRef = useRef(false);

  // hooks
  const { openUrl, getIsRefresh } = useOpenUrl();

  // effects
  useEffect(() => {
    let cancelled = false;

    // setup listener for appUrlOpen
    const setupListener = async () => {
      const handle = await CapacitorApp.addListener(
        'appUrlOpen',
        (data: URLOpenListenerEvent) => {
          if (cancelled || appUrlOpenRef.current) return;
          appUrlOpenRef.current = true;

          console.log('handling appUrlOpen', data);
          openUrl(data.url);

          appUrlOpenRef.current = false;
        },
      );

      if (!cancelled) {
        listenerRef.current = handle;
      } else {
        // if we’ve already unmounted by the time the listener was ready
        handle.remove();
      }
    };

    // initialize the listener then check for launch URL
    const init = async () => {
      await setupListener();

      if (cancelled || appUrlOpenRef.current) return;
      appUrlOpenRef.current = true;

      try {
        const data: AppLaunchUrl | undefined =
          await CapacitorApp.getLaunchUrl();
        if (cancelled || !data?.url || getIsRefresh(data.url)) return;

        console.log('handling launch url', data.url);
        openUrl(data.url);
      } finally {
        appUrlOpenRef.current = false;
      }
    };

    setTimeout(init, 0);

    return () => {
      cancelled = true;
      if (listenerRef.current) {
        listenerRef.current.remove();
      }
    };
  }, [openUrl, getIsRefresh]);

  return null;
};

export default UniversalLinkHandler;
